import { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { usePortailAuth } from "../lib/auth-context";
import { supabase } from "../lib/supabase";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = "text" }: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={event => onChange(event.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
    />
  );
}

function SectionPassword() {
  const { portailUser } = usePortailAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const changePassword = async () => {
    setStatus(null);
    if (next.length < 10) {
      setStatus({ type: "error", text: "Le nouveau mot de passe doit contenir au moins 10 caractères." });
      return;
    }
    if (next !== confirmation) {
      setStatus({ type: "error", text: "Les deux nouveaux mots de passe ne correspondent pas." });
      return;
    }
    if (!portailUser?.email) return;
    setSaving(true);
    const { error: verifyError } = await supabase.auth.signInWithPassword({ email: portailUser.email, password: current });
    if (verifyError) {
      setStatus({ type: "error", text: "Le mot de passe actuel est incorrect." });
      setSaving(false);
      return;
    }
    const { error: updateError } = await supabase.auth.updateUser({ password: next });
    setSaving(false);
    if (updateError) {
      setStatus({ type: "error", text: "Impossible de mettre à jour le mot de passe. Réessayez." });
      return;
    }
    setCurrent("");
    setNext("");
    setConfirmation("");
    setStatus({ type: "success", text: "Votre mot de passe a été mis à jour." });
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
        <KeyRound className="w-4 h-4 text-muted-foreground" />
        <div>
          <h2 className="text-sm font-semibold text-foreground">Mot de passe</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Modifiez votre accès personnel au portail.</p>
        </div>
      </div>
      <div className="p-5 max-w-xl space-y-4">
        {status && (
          <div className={`rounded-lg px-3 py-2.5 text-sm ${status.type === "success" ? "bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400" : "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400"}`}>
            {status.text}
          </div>
        )}
        <Field label="Mot de passe actuel">
          <Input value={current} onChange={setCurrent} type="password" placeholder="Votre mot de passe actuel" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nouveau mot de passe">
            <Input value={next} onChange={setNext} type="password" placeholder="10 caractères minimum" />
          </Field>
          <Field label="Confirmation">
            <Input value={confirmation} onChange={setConfirmation} type="password" placeholder="Répétez le nouveau mot de passe" />
          </Field>
        </div>
        <button onClick={changePassword} disabled={saving || !current || !next || !confirmation} className="flex items-center gap-2 px-4 py-2.5 text-sm text-white rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-60">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} {saving ? "Mise à jour…" : "Mettre à jour le mot de passe"}
        </button>
      </div>
    </div>
  );
}

export default function ParametresPage() {
  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Paramètres</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Gérez les réglages personnels de votre compte.</p>
      </div>
      <SectionPassword />
    </div>
  );
}